import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { GcImageComponent } from '../gc-image/gc-image.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthService, UserSignal } from '@app/services/auth.service';
import { UserService } from '@app/services/user.service';
import { AddMissingChargerMapDialogComponent } from '../add-missing-charger-map-dialog/add-missing-charger-map-dialog.component';
import { getUserCoordinates } from '@app/shared/utils/location';
import * as L from 'leaflet';
import { AddMissingChargerHoursDialogComponent } from '../add-missing-charger-hours-dialog/add-missing-charger-hours-dialog.component';
import { LocationType, StationHours as OriginalStationHours, Station } from "@app/services/station.interface";
import { daysOfWeek, WEEKDAYS } from '@app/shared/utils/consts';
import { formatTime } from '@app/shared/utils';
import { StationService } from '@app/services/station.service';
import { Origin, SearchResult } from '@app/services/model/search';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { SearchService } from '@app/services/search.service';

@Component({
  selector: 'app-add-missing-charger-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    MatIconModule,
    RouterLink,
    MatButtonModule,
    GcImageComponent,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatAutocompleteModule,
  ],
  templateUrl: './add-missing-charger-dialog.component.html',
  styleUrl: './add-missing-charger-dialog.component.scss'
})
export class AddMissingChargerDialogComponent implements OnInit {
  dialogRef = inject(MatDialogRef<AddMissingChargerDialogComponent>);
  @ViewChild('dialogContainer') dialogContainer!: ElementRef;

  imagePreviews: string[] = [];
  images: File[] = [];

  user = signal<UserSignal>(undefined);
  getUserPhoto = null;

  private map: L.Map | undefined;
  location: { latitude: number; longitude: number; } = {
    latitude: 37.0902,
    longitude: -95.7129,
  };
  hours: OriginalStationHours;
  address: SearchResult;
  daysOfWeek = daysOfWeek;
  WEEKDAYS = WEEKDAYS;
  formatTime = formatTime;
  state = signal<'initial' | 'loading' | 'success' | 'error'>('initial');

  locationTypes: LocationType[] = [];
  searchedAddresses: SearchResult[] = [];

  addressFieldTouched = false;
  mapLocationTouched = false;

  createdStationId;

  private _formBuilder = inject(FormBuilder);
  formGroup = this._formBuilder.group({
    name: ['', Validators.required],
    locationTypeId: [''],
    phone: [''],
    website: [''],
    address: [''],
  });

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private dialog: MatDialog,
    private stationService: StationService,
    private searchService: SearchService,
  ) {}

  ngOnInit(): void {
    this.user = this.authService.userSignal;
    this.getUserPhoto = this.userService.getUserPhoto

    getUserCoordinates().then(userLocation => {
      this.location = userLocation;
    }).finally(() => {
      this.map = L.map('map-preview', {
        attributionControl: false,
        zoomControl: false,
        dragging: false,
        touchZoom: false,
        scrollWheelZoom: false,
      }).setView([this.location.latitude, this.location.longitude], 17);
    });
    setTimeout(() => {
      this.initMap();
    }, 100);


    // fetch location types
    this.stationService.getLocationTypes().subscribe(types => {
      this.locationTypes = types;
    });

    this.formGroup.get('address').valueChanges.pipe(
      debounceTime(300), // delay input
      distinctUntilChanged(), // only trigger if value changed
      switchMap(query => this.searchAddress(query)) // replace with your service
    ).subscribe(res => {
      this.searchedAddresses = res.filter(r => r.isStation === false);
    });
  }

  private searchAddress(query: string) {
    return this.searchService.autocomplete(query, this.location);
  }

  initMap(): void {
    const tileLayerUrl = `https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`;
    L.tileLayer(tileLayerUrl).addTo(this.map);
  }

  close() {
    this.dialogRef.close();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    Array.from(input.files).forEach(file => {
      if (file.type.startsWith('image/')) {
        this.images.push(file);

        const reader = new FileReader();
        reader.onload = () => this.imagePreviews.push(reader.result as string);
        reader.readAsDataURL(file);
      }
    });
  }

  removeImage(img: any) {
    const index = this.imagePreviews.indexOf(img);
    this.imagePreviews.splice(index, 1);
    this.images.splice(index, 1);
  }

  openMapDialog() {
    const dialogRef = this.dialog.open(AddMissingChargerMapDialogComponent, {
      maxWidth: '500px',
      width: '500px',
      height: '845px',
      data: {
        latitude: this.location.latitude,
        longitude: this.location.longitude,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.latitude && result.longitude) {
        this.updateMapLocation(result);
        this.mapLocationTouched = true;

        // NOTE: we have decided not to do this
        /* if (!this.addressFieldTouched) {
          // TODO: fetch address with lat and lng and set it to the address field
          this.searchService.autocomplete('', {
            latitude: result.latitude,
            longitude: result.longitude,
          }).subscribe(res => {
            console.log(res);
          });
        } */
      }
    });
  }

  updateMapLocation(location: Origin) {
    this.location = {
      latitude: location.latitude,
      longitude: location.longitude,
    };
    this.map?.setView([location.latitude, location.longitude], 17);
  }

  openHoursDialog() {
    const dialogRef = this.dialog.open(AddMissingChargerHoursDialogComponent, {
      maxWidth: '500px',
      width: '500px',
      height: '845px',
      data: { hours: this.hours },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.hours) {
        this.hours = result?.hours;
      }
    });
  }

  getWeekDay(day: number) {
    return WEEKDAYS[daysOfWeek[day]];
  }

  submitForm() {
    this.state.set('loading');
    this.stationService.createStation({
      name: this.formGroup.value.name,
      featureGeometry: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [this.location.longitude, this.location.latitude],
        },
      },
      locationAddress: this.address?.address?.formattedStreetAddress ?? this.address?.address?.formattedAddress ?? this.formGroup.value.address,
      locationCity: this.address?.address?.city,
      locationState: this.address?.address?.county,
      locationCountry: this.address?.address?.addressNumber,
      locationPostal: this.address?.address?.postalCode,
      phone: this.formGroup.value.phone,
      website: this.formGroup.value.website,
      locationTypeId: parseInt(this.formGroup.value.locationTypeId),
    }).subscribe({
      next: (station: Station) => {
        if (!station) {
          this.state.set('error');
          return;
        }

        if (this.hours) {
          this.stationService.addHours(station?.id, this.hours).subscribe();
        }

        // upload images
        if (this.images.length > 0) {
          this.stationService.addStationPhotos(station.id, this.images);
        }

        this.createdStationId = station.id;
        this.state.set('success');
        this.resetFormState();
        this.hours = undefined;
        this.address = undefined;
        this.imagePreviews = [];
        this.scrollToTop();
      },
      error: () => {
        this.state.set('error');
        this.scrollToTop();
      },
    });
  }

  resetFormState() {
    this.formGroup.reset();
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsPristine();
      control?.markAsUntouched();
      control?.setErrors(null);
    });
  }

  onAddressSelected(event: MatAutocompleteSelectedEvent) {
    const address: SearchResult = event.option.value;
    this.formGroup.controls.address.setValue(address?.address?.formattedAddress);
    this.address = address;
    this.addressFieldTouched = true;

    if (!this.mapLocationTouched) {
      this.searchService.geocode(address.placeId, 'address').subscribe(geocodeResult => {
        this.updateMapLocation({
          latitude: geocodeResult.latitude,
          longitude: geocodeResult.longitude,
        });
      });
    }
  }

  scrollToTop() {
    this.dialogContainer.nativeElement.scrollTop = 0;
  }
}
